import React, { useState } from 'react';
import Nav from './nav';
import { useParams } from 'react-router-dom';
import axios from 'axios';

function CalenderPreview() {
  const { calenderId } = useParams();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // State to manage file uploads for each month
  const [files, setFiles] = useState({
    january: null,
    february: null,
    march: null,
    april: null,
    may: null,
    june: null,
    july: null,
    august: null,
    september: null,
    october: null,
    november: null,
    december: null,
  });

  // State to store preview URLs for each file
  const [previews, setPreviews] = useState({});

  // State to manage selected start month and year
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('2024'); // Default to 2024
  const [endMonth, setEndMonth] = useState('');
  const [endYear, setEndYear] = useState('');

  // Months array
  const months = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  // Handle start month change
  const handleStartMonthChange = (e) => {
    const selectedMonth = e.target.value;
    setStartMonth(selectedMonth);

    // Calculate end month and year
    const startIndex = months.indexOf(selectedMonth);
    const endIndex = (startIndex + 11) % months.length;

    setEndMonth(months[endIndex]);

    // If the endIndex loops back to the start of the months array, increment the year
    const calculatedEndYear =
      endIndex < startIndex ? parseInt(startYear) + 1 : startYear;
    setEndYear(calculatedEndYear);
  };

  // Handle year change
  const handleStartYearChange = (e) => {
    setStartYear(e.target.value);
    setEndYear(''); // Reset end year on year change
  };

  // Handle file change for each month with validation
  const handleFileChange = (e, month) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file type (e.g., only images)
      if (!file.type.startsWith('image/')) {
        alert('Only image files are allowed.');
        return;
      }

      // Validate file size (e.g., max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size should not exceed 2MB.');
        return;
      }

      // Create a preview URL for the file
      const previewUrl = URL.createObjectURL(file);

      setFiles((prevState) => ({
        ...prevState,
        [month]: file,
      }));

      setPreviews((prevState) => ({
        ...prevState,
        [month]: previewUrl,
      }));
    }
  };

  // Handle file removal for a specific month
  const handleRemoveFile = (month) => {
    setFiles((prevState) => ({
      ...prevState,
      [month]: null,
    }));

    setPreviews((prevState) => {
      // Revoke the object URL to free memory
      if (prevState[month]) {
        URL.revokeObjectURL(prevState[month]);
      }
      const newPreviews = { ...prevState };
      delete newPreviews[month];
      return newPreviews;
    });
  };

  // Handle form submission with validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate start month and year
    if (!startMonth || !startYear) {
      alert('Please select both start month and start year.');
      return;
    }

    // Validate at least one file is uploaded
    const hasFile = Object.values(files).some((file) => file !== null);
    if (!hasFile) {
      alert('Please upload at least one file.');
      return;
    }

    // Create a FormData object
    const formData = new FormData();
    formData.append('calenderId', calenderId);
    formData.append('startMonth', startMonth);
    formData.append('startYear', startYear);
    formData.append('endMonth', endMonth);
    formData.append('endYear', endYear);

    // Append files for each month
    Object.keys(files).forEach((month) => {
      if (files[month]) {
        formData.append(month, files[month]);
      }
    });

    // Send data to the backend
    try {
      const response = await axios.post(`${baseURL}/upload-calender`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Upload success:', response.data);
      alert('Files uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload files.');
    }
  };

  return (
    <>
      <Nav />
      <div className="container">
        <div className="row calender-row">
          <div className="col-sm-6">
            <div className="image-div">
              <img
                src={`${baseURL}/images/design/c1.jpeg`}
                className="calender-image-preview"
                alt="Calendar Preview"
              />
            </div>
          </div>
          <div className="col-sm-6">
            <h2>Calendar</h2>
            <p className="calender-desc">
              An academic calendar is a schedule of events that occur during an
              academic year, including important dates such as exam dates,
              holidays, and the last day of the semester.
            </p>

            {/* Year Selector */}
            <div className="mb-4">
              <label htmlFor="startYear" className="form-label">
                Select Start Year
              </label>
              <select
                id="startYear"
                className="form-control"
                value={startYear}
                onChange={handleStartYearChange}
              >
                <option value="2024">2024</option>
                <option value="2025">2025</option>
              </select>
            </div>

            {/* Month Selector */}
            <div className="mb-4">
              <label htmlFor="startMonth" className="form-label">
                Select Start Month
              </label>
              <select
                id="startMonth"
                className="form-control"
                value={startMonth}
                onChange={handleStartMonthChange}
                disabled={!startYear}
              >
                <option value="">-- Select Month --</option>
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month.charAt(0).toUpperCase() + month.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {startMonth && endMonth && (
              <div className="mb-4">
                <p>
                  <strong>Start Month:</strong>{' '}
                  {startMonth.charAt(0).toUpperCase() + startMonth.slice(1)}{' '}
                  {startYear}
                </p>
                <p>
                  <strong>End Month:</strong>{' '}
                  {endMonth.charAt(0).toUpperCase() + endMonth.slice(1)}{' '}
                  {endYear}
                </p>
              </div>
            )}

            {/* File Upload Form */}
            <form onSubmit={handleSubmit}>
              <div className="row">
                {Object.keys(files).map((month, index) => (
                  <div className="col-sm-6 mb-3" key={index}>
                    <label htmlFor={month} className="form-label">
                      {month.charAt(0).toUpperCase() + month.slice(1)}
                    </label>
                    {previews[month] ? (
                      <div className="preview-container">
                        <img
                          src={previews[month]}
                          alt={`${month} preview`}
                          className="img-thumbnail mb-2"
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveFile(month)}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        id={month}
                        className="form-control"
                        onChange={(e) => handleFileChange(e, month)}
                      />
                    )}
                  </div>
                ))}
              </div>
              <button type="submit" className="btn btn-primary">
                Submit Files
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default CalenderPreview;
